/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */
angular.module('bulbApp.unitModule')

/**
 * BulbUnitService gives access to the "currently displayed unit" on author pages, collections, and pages.
 * Also provides support for fetching the parent and immediate siblings of the current unit.
 */
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  .service('BulbUnitService', [
    '$q',
    '$location',
    'Restangular',
    'BulbBusyService',
    'SerialSaveService',
    '_',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    BulbUnitService,
  ]);

function BulbUnitService($q, $location, Restangular, BulbBusyService, SerialSaveService, _) {
  const that = this;

  this.currentUnit = {};
  this.parentUnit = {};
  this.previousSibling = {};
  this.nextSibling = {};

  const fetchingParentPromises = {};

  /**
   * Set the currentUnit to the specified Unit.
   * This method also resets/clears any information from the previous current unit.  This method should always be
   * used rather than updating the currentUnit directly.
   * @param unit Unit to use in setting the currentUnit.  null can be used to indicate there isn't a current unit.
   */
  this.setCurrentUnit = function setCurrentUnit(unit) {
    if (unit === null || unit !== this.currentUnit) {
      if (this.currentUnit && this.currentUnit.parentId) {
        // FUTURE Eventually, it would be nice to retain the parent if the new unit was also a child of the
        // previous parent.  However, we don't have a way of updating the parent when new units are created
        // so the data could get dirty with no way to update.  So, for now, we will fetch the parent, even if
        // we have already fetched it.
        delete fetchingParentPromises[this.currentUnit.parentId];
      }

      // Update the currentUnit;
      angular.copy(unit, this.currentUnit);

      // Reset the rest of the values.  Don't replace the reference, but empty the object properties:
      // TODO CLEAN : move the next three into an object called 'family' so we don't have to do copies?
      angular.copy({}, this.parentUnit);
      angular.copy({}, this.previousSibling);
      angular.copy({}, this.nextSibling);
    }
  };

  /**
   * Fetch the Parent (and immediate siblings, if they exist) for the current Unit.
   * This will set the service's values for parentUnit, previousSibling and nextSibling.
   * @returns {Promise} Promise which is resolved when the fetch completes.
   */
  this.fetchParent = function fetchParent(optionsArg) {
    const options = optionsArg ? _.clone(optionsArg) : {};
    // Get the 'context' query param value from the URL if there  is one, so we know if the navigation is in
    // the context of an assignment folder or not.
    const { context } = $location.search();
    const { assignmentFolderId } = this.currentUnit;
    if (!this.currentUnit || !this.currentUnit.parentId) {
      // there isn't a parent, so there is nothing to do, but caller may be expecting a promise, so give them
      // one
      const deferred = $q.defer();
      deferred.resolve(); // using resolve because there isn't an error, and the parent/siblings are set properly
      return deferred.promise;
    }
    const id = (context === 'assignment' && assignmentFolderId) ? assignmentFolderId : this.currentUnit.parentId;

    if (!fetchingParentPromises[id] || _.isEmpty(that.parentUnit)) {
      // only start the parent fetch if this is a currentUnit and a parent fetch for it hasn't already been
      // initiated.
      fetchingParentPromises[id] = Restangular.one('units', id).get()
        .then((response) => {
          const unit = response.unitDTOs[0];
          if (fetchingParentPromises[unit.id]) {
            // Only set the values if we are still have a reference to the promise, because otherwise
            // we have been reset, and the currentUnit has changed, so the information here is old, and can
            // be discarded.

            angular.copy(unit, that.parentUnit);
            // If presenting from collection then we are filtering out the compositeUnits(collection) inside the
            // collection and just present pages.
            setSiblingUnits(options.presentingCollection);
          }
        });
    }

    // Now return the promise so the caller can react once the parent has been fetched.
    return fetchingParentPromises[id];
  };

  /**
   * Reorder composite unit children
   * @param unit The updated composite unit.
   * @param destinationMovedChildIndex The destination index of the child being moved.
   * @returns {Promise} deferred promise.
   */
  this.compositeUnitReorder = function compositeUnitReorder(unit, destinationMovedChildIndex) {
    // Find the id of the moving unit
    const movingUnit = unit.children[destinationMovedChildIndex];
    const unitInfo = {
      moveTargetChildUnitId: null,
      moveChildUnitId: movingUnit.id,
    };
    if (destinationMovedChildIndex !== 0) {
      const previousUnit = unit.children[destinationMovedChildIndex - 1];
      unitInfo.moveTargetChildUnitId = previousUnit.id;
    }
    return SerialSaveService.save({
      save: () => Restangular.one('units', unit.id).one('reorder').patch(unitInfo),
    });
  };

  /**
   * Set the next and previous unit to display in collection present mode.
   * @param units units to be presented.
   */
  function setNextAndPreviousUnit(units) {
    if (units) {
      const index = units.findIndex((currentIndex) => currentIndex.id === that.currentUnit.id);
      angular.copy(index > 0 ? units[index - 1] : {}, that.previousSibling);
      angular.copy(index < units.length - 1 ? units[index + 1] : {}, that.nextSibling);
    }
  }

  /**
   * Set the siblings for the current unit.
   * @param presentingCollection true if presenting from collection else false.
   */
  function setSiblingUnits(presentingCollection) {
    // If presenting from collection then we are filtering out the compositeUnits(collection) inside the collection
    // and just present pages.
    if (presentingCollection) {
      const filteredUnits = that.parentUnit.children.filter((unit) => unit.unitType !== 'CompositeUnit');
      if (filteredUnits) {
        setNextAndPreviousUnit(filteredUnits);
      }
    } else {
      setNextAndPreviousUnit(that.parentUnit.children);
    }
  }

  this.deleteUnit = function deleteUnit(unit) {
    return SerialSaveService.save({
      showBusy: true,
      save: () => Restangular.one('units', unit.id).remove(),
    });
  };

  this.copyPageUnit = (unit, copyContents) => SerialSaveService.save({
    showBusy: true,
    save: () => Restangular.one('units', unit.id).customPOST(unit, 'copyDraftPage', { copyContents }),
  });
}
